"use client";

/**
 * LandingPagePublicView
 * Public-facing renderer for landing pages
 */

import { LandingPage } from "@/lib/database/landing-pages";
import { FetchToastProvider } from "@/components/providers/FetchToastProvider";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useEffect, useState, useCallback } from "react";
import { MessageCircle, Sparkles } from "lucide-react";
import ComponentRenderer from "./ComponentRenderer";
import ChatbotWidget from "./ChatbotWidget";

interface LandingPagePublicViewProps {
  landingPage: LandingPage;
  eventId: number;
  pageContext?: string; // "main" or subpage slug
}

export default function LandingPagePublicView({
  landingPage,
  eventId,
  pageContext = "main",
}: LandingPagePublicViewProps) {
  // Hooks
  const searchParams = useSearchParams();

  // States
  const [chatbotOpen, setChatbotOpen] = useState(false);

  // Derived values — enterprise theme override via query param (used by /dashboard/explore/sites)
  const primaryColor = searchParams.get("themeColor") || landingPage.primary_color;

  // Handlers
  const handleOpenChat = useCallback(() => {
    setChatbotOpen(true);
  }, []);

  // Scroll to hash anchor on page load
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = hash.slice(1);
    const hasSecondaryNav = landingPage.page_components.some(
      (c) => c.type === "navbar" && (c.config as Record<string, unknown>)?.secondaryLinks
    );
    const offset = hasSecondaryNav ? 120 : 80;

    const scrollToElement = () => {
      const el = document.getElementById(id);
      if (!el) return false;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
      return true;
    };

    // Retry multiple times as components may load asynchronously
    if (scrollToElement()) return;
    const delays = [300, 800, 1500];
    const timers = delays.map((delay) =>
      setTimeout(() => {
        scrollToElement();
      }, delay)
    );
    return () => timers.forEach(clearTimeout);
  }, [landingPage.page_components]);

  // Render
  return (
    <div className="min-h-screen bg-white">
      {/* Optional custom logo */}
      {landingPage.custom_logo_path && (
        <div className="bg-white border-b border-gray-200 py-4">
          <div className="max-w-7xl mx-auto px-4">
            <Image
              src={landingPage.custom_logo_path}
              width={150}
              height={50}
              alt={`${landingPage.name} logo`}
              className="h-12 w-auto"
            />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full">
        {landingPage.page_components.length === 0 ? (
          <div className="max-w-7xl mx-auto px-4 py-16 text-center">
            <p className="text-gray-500">This page is currently empty.</p>
          </div>
        ) : (
          <>
            {/* Navbar always renders first */}
            {landingPage.page_components
              .filter((c) => c.type === "navbar")
              .map((component) => (
                <ComponentRenderer
                  key={component.id}
                  component={component}
                  eventId={eventId}
                  primaryColor={primaryColor}
                  landingPageId={landingPage.landing_page_id}
                  pageContext={pageContext}
                />
              ))}
            {/* Other components render in order */}
            {landingPage.page_components
              .filter((c) => c.type !== "navbar")
              .map((component) => (
                <ComponentRenderer
                  key={component.id}
                  component={component}
                  eventId={eventId}
                  primaryColor={primaryColor}
                  landingPageId={landingPage.landing_page_id}
                  pageContext={pageContext}
                />
              ))}
          </>
        )}
      </div>

      {/* Chatbot Widget */}
      {/* TODO: Remove `true ||` once database columns are added */}
      {(true || landingPage.chatbot_enabled) && (
        <FetchToastProvider>
          <ChatbotWidget
            landingPage={landingPage}
            primaryColor={primaryColor}
            externalOpen={chatbotOpen}
            onExternalOpenHandled={() => setChatbotOpen(false)}
          />
        </FetchToastProvider>
      )}
    </div>
  );
}
